import * as Sentry from "@sentry/react";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router";
import { AuthProvider } from "~/context/AuthContext";
import { DroppedFilesProvider } from "~/context/DroppedFilesContext";
import { ErrorBoundaryFallback } from "~/root";
import "./instrument";
import AppRoutes from "./routes-config";

// After a new deploy, CDN may drop old hashed chunks. Users with a tab still on the previous
// bundle hit failed dynamic imports when navigating; Vite emits `vite:preloadError` for that.
// We recover by loading a fresh document. `location.reload()` often reuses a cached index.html
// and can loop many times, so we use `replace()` with a one-time query param to bust the cache.
//
// The reload only helps when the chunk is genuinely missing server-side. If the browser has a
// poisoned disk-cache entry for a chunk URL (an HTML fallback response cached as immutable), the
// fresh document requests the same URL, reads the same bad entry, and fires this event again. The
// cooldown below caps us at one attempt so that case surfaces the error boundary instead of
// reloading forever.
//
// IMPORTANT: we do NOT call `event.preventDefault()`. Doing so makes Vite's preload helper
// swallow the rejection and resolve the dynamic import with `undefined`, which causes React's
// `lazy` to throw a confusing `TypeError: Cannot read properties of undefined (reading 'default')`
// (see Sentry issue 7398609277). Letting Vite rethrow makes the import reject cleanly; the
// reload below still kicks in, and any brief error surfaced to Sentry is filtered via
// `ignoreErrors` in `app/instrument.ts`.

const viteRevalidateParam = "__vite_revalidate";
const viteRevalidateAttemptKey = "vite:last-revalidate-attempt";
const viteRevalidateCooldownMs = 60_000;

const url = new URL(window.location.href);
if (url.searchParams.has(viteRevalidateParam)) {
  url.searchParams.delete(viteRevalidateParam);
  window.history.replaceState({}, "", `${url.pathname}${url.search}${url.hash}`);
}

const claimRevalidateAttempt = () => {
  try {
    const lastAttempt = Number(
      window.sessionStorage.getItem(viteRevalidateAttemptKey)
    );

    if (lastAttempt && Date.now() - lastAttempt < viteRevalidateCooldownMs) {
      return false;
    }

    window.sessionStorage.setItem(viteRevalidateAttemptKey, String(Date.now()));
    return true;
  } catch {
    return false;
  }
};

window.addEventListener("vite:preloadError", () => {
  if (!claimRevalidateAttempt()) return;

  const next = new URL(window.location.href);
  next.searchParams.set(viteRevalidateParam, String(Date.now()));
  window.location.replace(next.toString());
});

const root = document.getElementById("root");
if (root) {
  createRoot(root, {
    onUncaughtError: Sentry.reactErrorHandler((error, errorInfo) => {
      console.warn("Uncaught error", error, errorInfo.componentStack);
    }),
    onCaughtError: Sentry.reactErrorHandler(),
    onRecoverableError: Sentry.reactErrorHandler(),
  }).render(
    <StrictMode>
      <BrowserRouter>
        <AuthProvider>
          <Sentry.ErrorBoundary
            fallback={({ resetError }) => (
              <ErrorBoundaryFallback resetError={resetError} />
            )}
            beforeCapture={(scope) => {
              scope.setTag("location", window.location.pathname);
              scope.setTag("errorBoundary", "root");
            }}
            onError={(error) => {
              console.error("React Error Boundary caught error:", error);
            }}
          >
            <DroppedFilesProvider>
              <AppRoutes />
            </DroppedFilesProvider>
          </Sentry.ErrorBoundary>
        </AuthProvider>
      </BrowserRouter>
    </StrictMode>
  );
}
